/**
 * عقودُ نقطة البيع — <b>هنا لا في `@shop/shared`</b>.
 *
 * وهذا حدُّ المالك بعينه: الحزمةُ المشتركةُ لما تشترك فيه التطبيقاتُ الثلاثة،
 * <b>وأسماءُ المجال تخصّ تطبيقَها</b>. وحالاتُ البيع والورديّة لا تعني
 * الإدارةَ ولا الموقعَ العامّ، فلو وُضعت هناك لَأُجبرا على معرفة ما لا يعنيهما.
 *
 * ⚠ <b>وهذه صورةُ ما يردّه الخادمُ لا مصدرُ حقيقة</b>: لا يُشتقّ منها سعرٌ ولا
 *   رصيدٌ ولا صلاحيّة. والحسابُ كلُّه هناك.
 */

/** صلاحيّاتُ البيع كما في `Perms.cs` حرفيًّا — <b>أسماءٌ لا قرار</b>. */
export const PosPerm = {
  customerView: 'sales.customer.view',
  customerEdit: 'sales.customer.edit',

  saleView: 'sales.sale.view',
  saleCreate: 'sales.sale.create',
  saleComplete: 'sales.sale.complete',
  saleCancel: 'sales.sale.cancel',

  priceOverride: 'sales.price.override',
  discountOverride: 'sales.discount.override',
  paymentView: 'sales.payment.view',

  returnView: 'sales.return.view',
  returnCreate: 'sales.return.create',
  returnPost: 'sales.return.post',
  refundPost: 'sales.refund.post',

  shiftOpen: 'sales.cash_session.open',
  shiftClose: 'sales.cash_session.close',
  shiftViewAll: 'sales.cash_session.view',
  cashPayout: 'sales.cash.payout',
  attendancePunch: 'hr.attendance.punch',

  costView: 'catalog.product.cost.view',
  productView: 'catalog.product.view',
  dashboardMine: 'dashboard.view.mine',
  dashboardAll: 'dashboard.view.all',
} as const

// ── الورديّة ──────────────────────────────────────────────────────────────

export type CashSession = {
  id: string
  sessionNo: string
  cashierId: string
  branchId: string
  status: 'OPEN' | 'CLOSED'
  openedAt: string
  openingCash: number
  closedAt: string | null
  countedCash: number | null
  expectedCash: number | null
  difference: number | null
  closeReason: string | null
  notes: string | null
}

export type CashSessionMethodLine = {
  code: string
  nameAr: string
  affectsCashDrawer: boolean
  taken: number
  given: number
}

export type CashSessionReport = {
  session: CashSession
  expectedCashNow: number
  cashIn: number
  cashOut: number
  nonCashIn: number
  nonCashOut: number
  salesCount: number
  /** قيمةُ ما بيع في الورديّة — لا النقدُ الداخل. */
  salesTotal: number
  returnsTotal: number
  netSales: number
  byMethod: CashSessionMethodLine[]
  /** ما خرج من الدرج مصروفًا — وقد طُرح من `expectedCashNow`. */
  payouts: number
  payoutLines: CashPayout[] | null
}

// ── المصروفُ من الدرج (`0048`) ─────────────────────────────────────────────

export type PayoutCategory = 'EMPLOYEE' | 'SUPPLIER' | 'SHOP' | 'INTERNET' | 'JAMIYA' | 'OTHER'

export type CashPayout = {
  id: string
  cashSessionId: string
  category: PayoutCategory
  partyKind: 'EMPLOYEE' | 'SUPPLIER' | null
  partyId: string | null
  partyName: string | null
  amount: number
  memo: string | null
  createdAt: string
  voided: boolean
  voidReason: string | null
}

export type PayeeOption = { id: string; name: string }

export type PayoutOptions = { employees: PayeeOption[]; suppliers: PayeeOption[] }

// ── الدوام (`0051`) ─────────────────────────────────────────────────────────

export type AttendanceRow = {
  id: string | null
  userId: string
  fullNameAr: string
  day: string
  checkIn: string | null
  checkOut: string | null
  workedHours: number | null
  overtimeHours: number
  overtimeOverride: number | null
  note: string | null
  openedShift: boolean
}

// ── البحث ────────────────────────────────────────────────────────────────

/** سطرُ نتيجةٍ من `/pos/lookup` — ما يُضاف إلى السلّة مباشرةً. */
export type PosHit = {
  variantId: string
  sku: string
  productName: string
  label: string
  barcode: string | null
  price: number | null

  /** أرضيّةُ المساومة — يردّها الخادمُ لمن يملك خصمًا أو تجاوزًا فقط. */
  minPrice: number | null
  currency: string
  onHand: number

  /** سعرُ القائمة حين يسري عرضٌ فيكون `price` سعرَه — يُشطب ليعرف الكاشيرُ أنّه عرض. */
  regularPrice?: number | null
}

// ── البيع ────────────────────────────────────────────────────────────────

export type SaleLineView = {
  id: string
  lineNo: number
  variantId: string
  sku: string
  productName: string
  qty: number
  unitId: string
  priceSource: 'LIST' | 'OVERRIDE' | 'NO_LIST_MANUAL'
  listPrice: number | null
  unitPrice: number
  discountAmount: number
  overrideReason: string | null
  taxRate: number
  taxInclusive: boolean
  netAmount: number
  taxAmount: number
  lineTotal: number

  /** محجوبةٌ من الخادم عمّن لا يملك `catalog.product.cost.view`. */
  unitCost: number | null
  totalCost: number | null
  costMissing: boolean
}

export type SalePaymentView = {
  id: string
  methodCode: string
  methodNameAr: string
  amount: number
  tendered: number | null
  change: number
  referenceNo: string | null
}

export type SaleHeader = {
  id: string
  saleNo: string | null
  status: 'DRAFT' | 'COMPLETED' | 'CANCELLED'
  branchId: string
  warehouseId: string
  cashSessionId: string | null
  customerId: string | null
  customerName: string | null
  customerPhone: string | null
  holdLabel: string | null
  currency: string
  subtotal: number
  discountTotal: number
  taxTotal: number
  total: number
  costTotal: number | null
  margin: number | null
  uncostedLines: number
  notes: string | null
  completedAt: string | null
  returnState: 'NONE' | 'PARTIALLY_RETURNED' | 'RETURNED'
}

export type SaleDetail = {
  header: SaleHeader
  lines: SaleLineView[]
  payments: SalePaymentView[]
}

/** سطرٌ يُرسل — <b>وبلا سعرٍ في الحالة العاديّة</b>: السعرُ من الخادم. */
export type SaleLineRequest = {
  variantId: string
  qty: number
  unitPrice?: number
  overrideReason?: string
  discountAmount?: number
}

export type PaymentRequest = {
  methodCode: string
  amount: number
  tendered?: number
  referenceNo?: string
}

// ── الفاتورة ─────────────────────────────────────────────────────────────

export type ReceiptLine = {
  lineNo: number
  productName: string
  sku: string
  qty: number
  unitPrice: number
  discount: number
  lineTotal: number
}

export type ReceiptPayment = {
  methodNameAr: string
  amount: number
  tendered: number | null
  change: number
}

/** ⚠ ولا كلفةَ فيها ولا هامش — والخادمُ لا يبنيهما أصلًا. */
export type SaleReceipt = {
  saleNo: string
  completedAt: string
  branchId: string
  sessionNo: string | null
  customerName: string | null
  customerPhone: string | null
  currency: string
  lines: ReceiptLine[]
  subtotal: number
  discountTotal: number
  taxTotal: number
  total: number
  payments: ReceiptPayment[]
  change: number
  notes: string | null
}

// ── المرتجع وردُّ المال ──────────────────────────────────────────────────

export type ReturnableLine = {
  saleLineId: string
  variantId: string
  sku: string
  productName: string
  soldQty: number
  returnedQty: number
  remainingQty: number
  /** السعرُ قبل الخصم — للعرض مشطوبًا حين يكون هناك خصم. */
  unitPrice: number
  discountAmount: number
  /** السعرُ الذي دُفع فعلًا — <b>وهو ما يُردّ</b>. */
  netUnitPrice: number
}

export type SalesReturnHeader = {
  id: string
  returnNo: string | null
  status: 'DRAFT' | 'POSTED'
  saleId: string
  saleNo: string | null
  returnKind: 'CUSTOMER_RETURN' | 'VOID'
  warehouseId: string
  cashSessionId: string | null
  currency: string
  total: number
  refunded: number

  /** <b>مشتقٌّ لا مخزَّن</b>: `PENDING` يعني بضاعةً عادت ومالًا ينتظر المشرف. */
  refundState: 'NONE' | 'PENDING' | 'SETTLED'
  reason: string | null
  notes: string | null
  postedAt: string | null
}

export type SalesReturnLineView = {
  id: string
  lineNo: number
  saleLineId: string
  variantId: string
  sku: string
  productName: string
  qty: number
  unitPrice: number
  lineTotal: number
  reasonCode: string | null
}

export type RefundView = {
  id: string
  methodCode: string
  methodNameAr: string
  amount: number
  referenceNo: string | null
}

export type SalesReturnDetail = {
  header: SalesReturnHeader
  lines: SalesReturnLineView[]
  refunds: RefundView[]
}

// ── العميل ───────────────────────────────────────────────────────────────

export type CustomerSummary = {
  id: string
  code: string
  nameAr: string
  nameEn: string | null
  phone: string | null
  phoneAlt: string | null
  email: string | null
  address: string | null
  notes: string | null
  isActive: boolean
  archivedAt: string | null
  archiveReason: string | null
}

export type SimilarCustomer = { id: string; code: string; nameAr: string; phone: string | null }

export type CustomerCreated = { customer: CustomerSummary; similar: SimilarCustomer[] }

// ── اللوحة ───────────────────────────────────────────────────────────────

export type PosDashboard = {
  day: string
  transactions: number
  revenue: number
  averageBasket: number
  cashIn: number
  nonCashIn: number
  returnsToday: number
  returnedToday: number

  /** محجوبان عمّن لا يملك `catalog.product.cost.view`. */
  costToday: number | null
  margin: number | null
  uncostedLines: number

  openDrafts: number
  openShifts: number
  pendingRefunds: number
  pendingRefundAmount: number
  negativeStockVariants: number
}

/** صنفٌ بِيع في الورديّة — مُجمَّعًا بكمّيّته ومبلغه. */
export type SoldItem = {
  variantId: string
  productName: string
  sku: string
  /** المباعُ ناقصَ المردود في هذه الورديّة — <b>وقد يكون سالبًا</b>. */
  qty: number
  amount: number
  returnedQty: number
}

/** مادّةٌ في فاتورة — بالسعر الذي بيعت به بعد الخصم. */
export type InvoiceItem = {
  productName: string
  sku: string
  qty: number
  unitPrice: number
  discount: number
  lineTotal: number
}

/** طريقةُ دفعٍ في فاتورة — والفاتورةُ قد تُدفع بأكثرَ من طريقة. */
export type InvoicePayment = { methodName: string; amount: number }

/** فاتورةٌ بموادّها وطرقِ دفعها — كما يردّها `/sales/shift-invoices`. */
export type InvoiceWithItems = {
  saleNo: string
  completedAt: string
  cashierName: string
  customerName: string | null
  lines: number
  discount: number
  total: number
  items: InvoiceItem[]
  payments: InvoicePayment[]
}
